/**
 * LeadGradeBadge Component
 * Displays lead quality grade (A-E) with professional visual styling
 * Includes tooltips, color coding, and score breakdown
 */

import React from 'react';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';

const GRADE_CONFIG = {
    A: {
        severity: 'success',
        label: 'Premium',
        description: 'Excellent lead quality with high conversion potential',
        color: '#22c55e',
        bgColor: '#f0fdf4',
        range: '90-100'
    },
    B: {
        severity: 'info',
        label: 'High',
        description: 'Good lead quality with strong potential',
        color: '#3b82f6',
        bgColor: '#eff6ff',
        range: '75-89'
    },
    C: {
        severity: 'warning',
        label: 'Standard',
        description: 'Average lead quality with moderate potential',
        color: '#f59e0b',
        bgColor: '#fffbeb',
        range: '60-74'
    },
    D: {
        severity: 'warning',
        label: 'Basic',
        description: 'Below average quality, may require more effort',
        color: '#f97316',
        bgColor: '#fff7ed',
        range: '40-59'
    },
    E: {
        severity: 'danger',
        label: 'Entry',
        description: 'Entry level quality, needs thorough verification',
        color: '#ef4444',
        bgColor: '#fef2f2',
        range: '0-39'
    }
};

const LeadGradeBadge = ({
    grade,
    score = null,
    size = 'normal',
    showLabel = false,
    showScore = false,
    showTooltip = true,
    className = ''
}) => {
    const normalizedGrade = grade?.toUpperCase() || 'E';
    const config = GRADE_CONFIG[normalizedGrade] || GRADE_CONFIG.E;
    const displayGrade = normalizedGrade || '-';
    const uniqueId = `grade-badge-${Math.random().toString(36).substr(2, 9)}`;

    // Compact badge for small size
    if (size === 'small') {
        return (
            <>
                <Badge
                    id={uniqueId}
                    value={displayGrade}
                    severity={config.severity}
                    className={className}
                />
                {showTooltip && (
                    <Tooltip target={`#${uniqueId}`} position="top">
                        <div className="text-center">
                            <div className="font-semibold">Grade {displayGrade} - {config.label}</div>
                            {score !== null && <div className="text-sm">Score: {score}/100</div>}
                        </div>
                    </Tooltip>
                )}
            </>
        );
    }

    // Large decorative badge
    if (size === 'large') {
        return (
            <div className={`flex align-items-center gap-3 ${className}`}>
                <div
                    className="flex align-items-center justify-content-center border-round-lg font-bold text-2xl"
                    style={{
                        width: '56px',
                        height: '56px',
                        backgroundColor: config.bgColor,
                        color: config.color,
                        border: `2px solid ${config.color}`
                    }}
                >
                    {displayGrade}
                </div>
                <div>
                    <div className="font-semibold text-900 text-lg">{config.label} Quality</div>
                    {score !== null && (
                        <div className="text-500 text-sm">Score: {score}/100</div>
                    )}
                    <div className="text-400 text-xs mt-1">{config.description}</div>
                </div>
            </div>
        );
    }

    // Normal tag with optional label and score
    return (
        <div className={`flex align-items-center gap-2 ${className}`}>
            <Tag
                id={uniqueId}
                value={`Grade ${displayGrade}`}
                severity={config.severity}
            />
            {showLabel && (
                <span className="text-600 font-medium text-sm">
                    {config.label}
                </span>
            )}
            {showScore && score !== null && (
                <span className="text-500 text-sm">
                    ({score}/100)
                </span>
            )}
            {showTooltip && !showLabel && (
                <Tooltip target={`#${uniqueId}`} position="top">
                    <div className="text-center p-1">
                        <div className="font-semibold mb-1">{config.label} Quality</div>
                        <div className="text-xs text-300">{config.description}</div>
                        {score !== null && (
                            <div className="text-sm mt-1">Score: {score}/100</div>
                        )}
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default LeadGradeBadge;
